import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Chip,
  Paper,
  Box,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  QuestionDto,
  AnswerType,
  QuestionDataKey,
  ListItemFieldDefinition,
} from '../../types/questions';

interface QuestionFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (question: Partial<QuestionDto>) => void;
  question: QuestionDto | null;
  professionKey?: string;
  language?: string;
}

const QuestionFormModal: React.FC<QuestionFormModalProps> = ({
  open,
  onClose,
  onSave,
  question,
}) => {
  const [formData, setFormData] = useState<Partial<QuestionDto>>({
    questionKey: '',
    questionName: '',
    question: '',
    questionShorten: '',
    answerType: AnswerType.TEXT,
    isRequiredAnswer: true,
    requiresVerification: false,
    options: [],
    listItemSchema: [],
  });

  const [newOption, setNewOption] = useState({ key: '', label: '' });
  const [newListField, setNewListField] = useState<ListItemFieldDefinition>({
    fieldName: '',
    label: '',
    fieldType: AnswerType.TEXT,
    isRequired: false,
  });

  useEffect(() => {
    if (question) {
      setFormData(question);
    } else {
      setFormData({
        questionKey: '',
        questionName: '',
        question: '',
        questionShorten: '',
        answerType: AnswerType.TEXT,
        isRequiredAnswer: true,
        requiresVerification: false,
        options: [],
        listItemSchema: [],
      });
    }
  }, [question]);

  const handleChange = (field: keyof QuestionDto, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Note: messageType was removed; backend does not accept it
  };

  const handleAddOption = () => {
    if (newOption.key && newOption.label) {
      setFormData(prev => ({
        ...prev,
        options: [...(prev.options || []), newOption],
      }));
      setNewOption({ key: '', label: '' });
    }
  };

  const handleRemoveOption = (index: number) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options?.filter((_, i) => i !== index),
    }));
  };

  const handleAddListField = () => {
    if (newListField.fieldName && newListField.label) {
      setFormData(prev => ({
        ...prev,
        listItemSchema: [...(prev.listItemSchema || []), newListField],
      }));
      setNewListField({
        fieldName: '',
        label: '',
        fieldType: AnswerType.TEXT,
        isRequired: false,
      });
    }
  };

  const handleRemoveListField = (index: number) => {
    setFormData(prev => ({
      ...prev,
      listItemSchema: prev.listItemSchema?.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
  };

  const showOptions = formData.answerType === AnswerType.RADIO || formData.answerType === AnswerType.MULTISELECT;
  const showListSchema = formData.answerType === AnswerType.LIST_OBJECT;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              {question ? 'Edit Question' : 'Add New Question'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              All questions must be written in English
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <TextField
            label="Question Key"
            value={formData.questionKey}
            onChange={(e) => handleChange('questionKey', e.target.value)}
            fullWidth
            required
            helperText="Unique identifier for this question"
          />

          <TextField
            label="Question Name"
            value={formData.questionName}
            onChange={(e) => handleChange('questionName', e.target.value)}
            fullWidth
            required
            helperText="Internal name for the question"
          />

          <TextField
            label="Question Text (English)"
            value={formData.question}
            onChange={(e) => handleChange('question', e.target.value)}
            fullWidth
            required
            multiline
            rows={3}
            helperText="The question as shown to users - must be in English"
          />

          <TextField
            label="Short Description"
            value={formData.questionShorten}
            onChange={(e) => handleChange('questionShorten', e.target.value)}
            fullWidth
            required
            helperText="Brief description for lists and summaries"
          />

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Answer Type</InputLabel>
              <Select
                value={formData.answerType}
                onChange={(e) => handleChange('answerType', e.target.value)}
                label="Answer Type"
              >
                {Object.values(AnswerType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Data Key</InputLabel>
              <Select
                value={formData.questionDataKey || ''}
                onChange={(e) => handleChange('questionDataKey', e.target.value || null)}
                label="Data Key"
              >
                <MenuItem value="">None</MenuItem>
                {Object.values(QuestionDataKey).map((key) => (
                  <MenuItem key={key} value={key}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRequiredAnswer}
                  onChange={(e) => handleChange('isRequiredAnswer', e.target.checked)}
                />
              }
              label="Required Answer"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiresVerification}
                  onChange={(e) => handleChange('requiresVerification', e.target.checked)}
                />
              }
              label="Requires Verification"
            />
          </Stack>

          {showOptions && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Options
              </Typography>
              <Stack spacing={2}>
                {formData.options?.map((option, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Chip label={`${option.key}: ${option.label}`} onDelete={() => handleRemoveOption(index)} />
                  </Stack>
                ))}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Key"
                    value={newOption.key}
                    onChange={(e) => setNewOption({ ...newOption, key: e.target.value })}
                  />
                  <TextField
                    size="small"
                    placeholder="Label"
                    value={newOption.label}
                    onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddOption}
                    disabled={!newOption.key || !newOption.label}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          {showListSchema && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                List Item Schema
              </Typography>
              <Stack spacing={2}>
                {formData.listItemSchema?.map((field, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${field.fieldName}: ${field.label} (${field.fieldType})`}
                      onDelete={() => handleRemoveListField(index)}
                    />
                  </Stack>
                ))}
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Field Name"
                    value={newListField.fieldName}
                    onChange={(e) => setNewListField({ ...newListField, fieldName: e.target.value })}
                  />
                  <TextField
                    size="small"
                    placeholder="Label"
                    value={newListField.label}
                    onChange={(e) => setNewListField({ ...newListField, label: e.target.value })}
                  />
                  <FormControl size="small">
                    <Select
                      value={newListField.fieldType}
                      onChange={(e) => setNewListField({ ...newListField, fieldType: e.target.value as AnswerType })}
                    >
                      <MenuItem value={AnswerType.TEXT}>TEXT</MenuItem>
                      <MenuItem value={AnswerType.NUMBER}>NUMBER</MenuItem>
                      <MenuItem value={AnswerType.DATE}>DATE</MenuItem>
                      <MenuItem value={AnswerType.BOOLEAN}>BOOLEAN</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddListField}
                    disabled={!newListField.fieldName || !newListField.label}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.questionKey || !formData.question}
        >
          {question ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionFormModal;
